import { useCallback, useMemo } from 'react';
import secureLocalStorage from 'react-secure-storage';
import { Link } from 'react-router-dom';
import dayjs from 'dayjs';
import { Box, Typography } from '@mui/material';
import { useDispatch } from 'react-redux';
import { StoredFields } from '../../enums/StoredFields';
import DateHelper from '../../helpers/date.helper';
import classes from './ArticlePreview.module.scss';
import { ArticlePreviewProps } from '../../interfaces/props/ArticlePreviewProps';
import { setAuthor } from '../../../store/filters/filters.slice';
import URLHelper from '../../helpers/url.helper';
import { RouteNames } from '../../enums/RouteNames';
import ImageWrapper from '../../components/image-wrapper/ImageWrapper';

const fallbackImage = <Box
  component={'img'}
  className='image-placeholder-single-article'
/>;

const ArticlePreview = ({ image, title, summary, createdAt, articleURL, siteURL, author, numberOfWords, videoLength, channel, iFrameURL }: ArticlePreviewProps) => {
  const dispatch = useDispatch();
  const showFeatureImageHandler = useCallback(() => {
    return image.length > 0 ?
      <ImageWrapper
        className={`${classes['article-image']} w-100`}
        alt='Article Image'
        src={image}
        fallbackElement={fallbackImage}
      />
      :
      fallbackImage
  }, [image]);

  const calculateTimeDifference = useMemo(() => {
    return DateHelper.calculateTimeDifferenceFromNow(dayjs(createdAt));
  }, [createdAt]);

  const filterByAuthorHandler = (author: string) => {
    dispatch(setAuthor(author));
  };

  const showVideo = useMemo(() => {
    return iFrameURL ?
      <iframe
        className={classes['iframe-yt']}
        width='100%'
        title={title}
        src={iFrameURL}
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
        allowFullScreen
      />
      :
      null
  }, [iFrameURL, title]);

  const showInformations = useMemo(() => {
    return <>
      {
        numberOfWords > 0 && videoLength === null ?
          <Typography>
            Word count: {numberOfWords}
          </Typography>
          :
          null
      }
      {
        videoLength ?
          <Typography>
            Video length: {videoLength} seconds
          </Typography>
          :
          null
      }
      {
        channel ?
          <Typography>
            Channel: {channel}
          </Typography>
          :
          null
      }
    </>
  }, [channel, numberOfWords, videoLength]);

  return (
    <>
      <Box className='d-flex flex-sm-row flex-column gap-3 justify-content-between pb-3'>
        <Box className='d-flex flex-column flex-sm-row gap-3 ps-2 justify-content-sm-between'>
          <Typography alignSelf='start'>
            Original article on: <Link
              className={`${classes.link} ps-1`}
              to={articleURL}
              target='_blank'
              rel='noopener noreferrer'
            >
              {`${URLHelper.getDomainName(siteURL)}`}
            </Link>
          </Typography>
          {showInformations}
          <Box>
            <Link
              className='d-flex'
              style={{ textDecoration: 'none', color: 'black' }}
              onClick={() => filterByAuthorHandler(author)}
              to={RouteNames.HOME}
            >
              <Typography color='text.disabled'>
                By {author}
              </Typography>
            </Link>
          </Box>
        </Box>
        <Typography alignSelf='start' color='grey' className='ps-2'>
          {calculateTimeDifference}
        </Typography>
      </Box>
      {
        iFrameURL ? showVideo : showFeatureImageHandler()
      }
      <Box className={`${classes.cardContent} pt-1`}>
        <Box className='d-flex flex-column flex-wrap'>
          <Typography align='left' color='grey'>
            {String(secureLocalStorage.getItem(StoredFields.IMAGE_DISCLAIMER))}
          </Typography>
          <Typography className='pt-4' variant='h4' align='left' fontWeight='bold'>
            {title}
          </Typography>
        </Box>
        <Typography className='pt-3'>
          {summary}
        </Typography>
      </Box>
    </>
  );
};

export default ArticlePreview;
